/**
 * External Access API Client (EPIC-AUTH-008)
 *
 * Endpoints:
 * - GET    /api/tenant-management/orgs/{orgId}/users/external
 * - POST   /api/tenant-management/orgs/{orgId}/users/external
 * - DELETE /api/tenant-management/orgs/{orgId}/users/external/{userId}
 * - PUT    /api/tenant-management/orgs/{orgId}/users/external/{userId}/role
 *
 * Reading follows the same authority as the members roster — your own
 * organization is yours to read, another one needs staff. Granting, revoking
 * and changing the role are staff acts (D1), which the backend enforces on the route; `canManage` on
 * the list response is what the UI gates its controls on.
 *
 * The account is named by its ZITADEL user id, never by email (D2). The id is
 * on screen in the members grid (REQ-AUTH-078), which is where it is copied
 * from.
 *
 * @module api/ExternalAccessApiClient
 */

import { httpGet, httpPost, httpPut, httpDelete, type HttpOptions } from './http';
import type {
  ExternalAccessResponse,
  ExternalAccessMutationResponse,
  UserRole,
} from '../types';

const TM_API_BASE = '/api/tenant-management';

/**
 * Builds the org-scoped external-access collection path. The org id is
 * URL-encoded so an id containing reserved characters cannot break out of its
 * path segment.
 */
function orgExternalPath(orgId: string): string {
  return `${TM_API_BASE}/orgs/${encodeURIComponent(orgId)}/users/external`;
}

/**
 * The external-access operations the portal needs.
 */
export interface ExternalAccessApiClient {
  listExternalAccess(orgId: string): Promise<ExternalAccessResponse>;
  grantExternalAccess(
    orgId: string,
    userId: string,
    role: UserRole,
  ): Promise<ExternalAccessMutationResponse>;
  revokeExternalAccess(
    orgId: string,
    userId: string,
  ): Promise<ExternalAccessMutationResponse>;
  setExternalAccessRole(
    orgId: string,
    userId: string,
    role: UserRole,
  ): Promise<ExternalAccessMutationResponse>;
}

export class RealExternalAccessApiClient implements ExternalAccessApiClient {
  private readonly options?: HttpOptions;

  constructor(options?: HttpOptions) {
    this.options = options;
  }

  /**
   * GET /orgs/{orgId}/users/external
   *
   * Not paginated: the backend answers with a bounded scan and says whether it
   * was cut short. See the `truncated` flag.
   */
  async listExternalAccess(orgId: string): Promise<ExternalAccessResponse> {
    return httpGet<ExternalAccessResponse>(orgExternalPath(orgId), this.options);
  }

  /**
   * POST /orgs/{orgId}/users/external
   */
  async grantExternalAccess(
    orgId: string,
    userId: string,
    role: UserRole,
  ): Promise<ExternalAccessMutationResponse> {
    return httpPost<ExternalAccessMutationResponse>(
      orgExternalPath(orgId),
      { userId, role },
      this.options,
    );
  }

  /**
   * DELETE /orgs/{orgId}/users/external/{userId}
   *
   * Removes only this organization's grant: the account survives and its access
   * to every other organization survives with it.
   */
  async revokeExternalAccess(
    orgId: string,
    userId: string,
  ): Promise<ExternalAccessMutationResponse> {
    return httpDelete<ExternalAccessMutationResponse>(
      `${orgExternalPath(orgId)}/${encodeURIComponent(userId)}`,
      this.options,
    );
  }

  /**
   * PUT /orgs/{orgId}/users/external/{userId}/role
   *
   * Rewrites the role on this organization's grant only.
   */
  async setExternalAccessRole(
    orgId: string,
    userId: string,
    role: UserRole,
  ): Promise<ExternalAccessMutationResponse> {
    return httpPut<ExternalAccessMutationResponse>(
      `${orgExternalPath(orgId)}/${encodeURIComponent(userId)}/role`,
      { role },
      this.options,
    );
  }
}
